import React from 'react'
import { useRoute } from '@react-navigation/native'

import { buildComponent } from 'src/components/factory'
import { useAppearance } from 'src/hooks/use-appearance'
import { useStoreState } from 'src/hooks/state'
import { Category } from 'src/state/models/audio'

import * as Elements from 'src/components/elements'
import * as Semantics from 'src/components/semantics'
import * as UI from 'native-base'

export const CategoryDetail = buildComponent()
  .withLocale('screens.connected.category-detail.screen')
  .withLifecycle(() => {
    const { slug } = (useRoute().params ?? {}) as { slug: string }
    const allCategories = useStoreState(
      (store) => store.space.info?.subscription?.audio?.categories ?? [],
    )
    const deviceLocale = useStoreState((store) => store.utils.device.locale)

    // Trouver la catégorie sélectionnée
    const category: Category | undefined = allCategories.find(
      (c: Category) => c.slug === slug,
    )

    useAppearance({
      action: 'back',
      title: category?.short[deviceLocale] || category?.short.fr || '',
    })

    // Trouver les sous-catégories (catégories qui ont cette catégorie comme parent)
    const subcategories: Category[] = allCategories.filter(
      (c: Category) => c.parent?.slug === slug,
    )

    // Fonction pour déterminer la route de destination selon le type
    const getDestinationRoute = (categoryType: string) => {
      switch (categoryType) {
        case 'book':
          return '/audio/audiobooks'
        case 'theme':
          return '/audio/sophrology'
        case 'background':
          return '/audio/backgrounds'
        default:
          return '/category'
      }
    }

    return {
      category,
      subcategories,
      hasSubcategories: subcategories.length > 0,
      deviceLocale,
      slug,
      getDestinationRoute,
      allCategories,
    }
  })
  .withRender(({ lifecycle }) => {
    if (!lifecycle.category) {
      return (
        <Elements.AudioView title="Catégorie non trouvée">
          {() => (
            <Semantics.PaddedScrollView centered px={2}>
              <UI.Text color="white">
                Catégorie non trouvée. Slug: {lifecycle.slug}
              </UI.Text>
            </Semantics.PaddedScrollView>
          )}
        </Elements.AudioView>
      )
    }

    const category = lifecycle.category

    return (
      <Elements.AudioView title={''}>
        {() => {
          if (lifecycle.hasSubcategories) {
            // Afficher les sous-catégories
            return lifecycle.subcategories.map((subcategory: Category) => {
              // Vérifier si cette sous-catégorie a elle-même des sous-catégories
              const hasSubSubcategories = lifecycle.allCategories.some(
                (c: Category) => c.parent?.slug === subcategory.slug,
              )

              return (
                <Elements.AudioOptionMain
                  key={subcategory.slug}
                  testID={`subcategory-${subcategory.slug}`}
                  to={
                    hasSubSubcategories
                      ? '/category/detail'
                      : lifecycle.getDestinationRoute(subcategory.type)
                  }
                  params={
                    hasSubSubcategories
                      ? { slug: subcategory.slug }
                      : {
                          category: subcategory.slug,
                          categoryType: subcategory.type,
                        }
                  }
                  imageSource={`images/categories/${subcategory.slug}/image.webp`}
                  title={subcategory.short}
                  description={subcategory.long}
                />
              )
            })
          } else {
            // Si pas de sous-catégories, naviguer vers le contenu audio selon le type
            return (
              <Elements.AudioOptionMain
                key={category.slug}
                to={lifecycle.getDestinationRoute(category.type)}
                params={{
                  category: category.slug,
                  categoryType: category.type,
                }}
                imageSource={`images/categories/${category.slug}/image.webp`}
                title={category.short}
                description={category.long}
              />
            )
          }
        }}
      </Elements.AudioView>
    )
  })
